// ═══════════════════════════════════════════════════════════════════
// imperaMotion.js — THE THIRTY-SIX BRANDS · H24 · Terror TCG
// One score per Impera. Every one written off the card's own printed
// effect, never off its Ilk.
// ═══════════════════════════════════════════════════════════════════
//
// caP'n (Aug 3): "a cool animation for every single Impera, U'hlti,
// action, etc... don't want to flood the game with visuals but yeah."
//
// Both halves of that sentence are load-bearing and they pull against
// each other, which is the actual design problem. Thirty-six unique
// brands, each recognisable — and a board that does not become a
// fireworks display when four of them resolve in one Response Window.
//
// ── HOW THE TENSION IS RESOLVED: SHORT, AND SPECIFIC ───────────────
// An Impera is not the loudest thing that happens on a turn and must
// never look like it is. So every score in this file is between 520 and
// 900ms, uses three to six cues, and spends nearly all of its budget on
// the ONE image that makes that card itself. The Signature is allowed
// to be a war crime. A brand is a brand.
//
// The discipline that makes thirty-six of them cohere is `brand()` in
// motionKit: a contracting iris, on every single one, no exceptions —
// H23's law, kept as code so it cannot be forgotten. What the eye
// learns after four or five Imperas is *that shape means a Soul just
// took a permanent mark*, and from then on the specific image on top of
// it is free information rather than noise.
//
// ── THE READING ORDER THAT PRODUCED THESE ──────────────────────────
// For each card: the effect text first, then the card NAME, then the
// species' flavour, and the score answers whichever of the three has
// the strongest image. Usually it is the name — "Diamond Skin",
// "Binary Star", "Weeping Dagger" are already animation directions.
// When the name is abstract ("Relentless", "Hope") the effect text
// carries it. When both are abstract, the species does.
//
// ⚠ NAMING. The keys here are CARD IDS (`p_*`) and those never change —
// vyren-law. The card TYPE used to be `Primera` and is `Impera` at the
// source now, matching the canon; a Primera is the innate STAMP and
// never a card. This file speaks Impera in its prose either way.
// ═══════════════════════════════════════════════════════════════════

import {
  P, score, brand, toll, wound, SHAKE,
  SURGE, beam, slash, shock, motes, pillar,
  spiral, wave, bloom, stream, glyph, rune,
  veil, shatter, chain, coin, crack, vortex,
  embers, lattice, halo, star, rift, echoes,
  sever, constellation, mirror, plume, thorn
} from "./motionKit";

// ── A LOCAL SHORTHAND ──────────────────────────────────────────────
// Almost every cue in this file is anchored to the BEING WEARING THE
// BRAND, not to a victim — an Impera is a thing that happens to your
// own card. So `S` is "on me", declared once, rather than `to: "self"`
// written 140 times.
const S = { to: "self" };

export const IMPERAS = {

// ═══════════════════════════════════════════════════════════════════
// SPACE · HUMANOID
// ═══════════════════════════════════════════════════════════════════

  // ── H-01 · NATURAL GLOW ───────────────────────────────────────────
  // "This being is Intangible." Intangibility appears five times across
  // the 36 and it is the single most important shape in this file to
  // get right, because a player must be able to tell at a glance that a
  // card can no longer be touched. The answer is the VEIL, every time —
  // a dome that closes over the being. What differs per card is the
  // colour and what comes with it.
  //
  // H-01's is the gentlest of the five: "these beings choose words as
  // their first resort." No snap, no mark. It simply stops being
  // available, politely.
  p_h01_1: score("Natural Glow", [
    ...brand(P.starWhite),
    veil(120, P.starCore, { ...S, ms: 620, r: 1.25, size: 0.78 }),
    motes(240, P.starWhite, { ...S, ms: 460, n: 18, r: 1.1, size: 0.06, tone: "in" }),
  ], 760),

  // ── H-01 · NATURAL LYT ────────────────────────────────────────────
  // "Choose 1 of your beings, it must be ruined before your adversary
  // can attack another being." A taunt — and a taunt is not a defensive
  // effect, it is an ADVERTISEMENT. So this one is the opposite of the
  // veil: the chosen being lights up like a flare and throws chains
  // across the seam, binding the adversary's attention to it.
  p_h01_2: score("Natural Lyt", [
    ...brand(P.starCore),
    star(150, P.starWhite, { ...S, ms: 420, r: 2.0, size: 0.06 }),
    chain(230, P.cyanHot, { from: "self", to: "target", ms: 520, r: 0.85, size: 0.1 }),
    shock(300, P.starCore, { ...S, ms: 460, size: 0.2, r: 2.2, tone: 0.6 }),
  ], 820),

  // ── H-01 · MORNING STAR ───────────────────────────────────────────
  // "Add 1 Astro Being to your hand from your Grimoire or Necropolis."
  // A search — the vortex verb — and the name hands over the image for
  // free. So: reach in, and one star comes back out. The star fires on
  // the vortex's last beat, which is the moment the hand closes on it.
  p_h01_3: score("Morning Star", [
    ...brand(P.starWhite),
    vortex(130, P.nebula, { ...S, ms: 600, r: 1.35, n: 46, spin: 5.5, size: 0.85 }),
    star(520, P.starWhite, { ...S, ms: 420, r: 1.7, size: 0.055 }),
    bloom(540, P.starCore, { ...S, ms: 280, r: 0.8 }),
  ], 900),

  // ── H-09 · COSMIC CASBAH ──────────────────────────────────────────
  // "Your beings gain +1 Lyf, for every Astro Being you have in
  // combat." A scaling buff keyed to how many friends are on the field
  // — so the image is a CONSTELLATION being drawn: separate points
  // discovering they are connected. The lattice assembling is that
  // exactly, and the wave carries it out to the rest of the board,
  // because this Impera is the rare one that buffs everybody.
  p_h09_1: score("Cosmic Casbah", [
    ...brand(P.cyanHot),
    // Was a lattice, which means BUILT — and this card does not build
    // anything, it notices that your Astro Beings are ALREADY related
    // and pays you for it. The constellation draws its links one at a
    // time, and that sequence is the whole difference.
    constellation(140, P.space, { ...S, ms: 680, r: 1.1, n: 7, size: 0.095 }),
    wave(420, P.nebula, { from: "self", to: "back", ms: 520, r: 4.4, size: 0.42, tone: 0.55 }),
    ...toll(P.cyanHot, 520, 1, "self"),
  ], 900),

  // ── H-09 · BINARY STAR ────────────────────────────────────────────
  // "Add 2 Astro Beings, up to Lvl 2, from your Grimoire or Necropolis
  // to your hand." TWO. The card is named for a two-star system and it
  // fetches exactly two things, so the score is the only one in the
  // file that fires the same cue twice at a deliberate offset — two
  // stars, out of phase, orbiting one throat.
  p_h09_2: score("Binary Star", [
    ...brand(P.starCore),
    vortex(120, P.space, { ...S, ms: 580, r: 1.2, n: 40, spin: 6.5, size: 0.8 }),
    star(430, P.starWhite, { ...S, ms: 400, r: 1.5, size: 0.05, tone: 22 }),
    star(560, P.cyanHot, { ...S, ms: 400, r: 1.5, size: 0.05, tone: -22 }),
    bloom(600, P.starCore, { ...S, ms: 300, r: 0.95 }),
  ], 960),

  // ── H-09 · RADIANT ANIMA ──────────────────────────────────────────
  // "Nullify 1 Lyt Attack." Nullification is the VEIL verb, always —
  // but this is a Space nullify from a line of self-proclaimed Generals
  // of the stars, so it does not merely refuse, it refuses BRIGHTLY.
  // The star fires INSIDE the dome, which is a shape nothing else in
  // the game makes.
  p_h09_3: score("Radiant Anima", [
    ...brand(P.starWhite),
    veil(110, P.starWhite, { ...S, ms: 560, r: 1.4, size: 0.8 }),
    star(230, P.cyanHot, { ...S, ms: 380, r: 1.6, size: 0.055 }),
    shock(280, P.starCore, { ...S, ms: 440, size: 0.22, r: 2.0, tone: 0.7 }),
  ], 780),

  // ── XULAN · DIAMOND SKIN ──────────────────────────────────────────
  // "This being gains +2 Lyf." The plainest effect on any Impera, and
  // the name does all the work: facets closing over a body. The lattice
  // is doing something slightly different here than in Cosmic Casbah —
  // tight, small, hugging the card rather than spanning the field — and
  // that is the whole reason `r` and `size` are parameters.
  p_xulan_1: score("Diamond Skin", [
    ...brand(P.scalpel),
    lattice(110, P.scalpel, { ...S, ms: 560, r: 0.62, n: 12, size: 0.038 }),
    star(420, P.starWhite, { ...S, ms: 320, r: 0.9, size: 0.04 }),
  ], 720),

  // ── XULAN · NATURAL SCALPEL ───────────────────────────────────────
  // "When this being Lyt Attacks, dim your adversary's Anima by 1."
  // The Xulan line's whole identity is the single clean cut — its Lyt
  // Attack is already The Incision — so its Impera is the instrument
  // rather than the wound. One sever, thin and unhurried, and no toll
  // at all: this is the only Impera in the file that deliberately skips
  // the third beat, because a scalpel is quiet and the silence is the
  // characterisation.
  p_xulan_2: score("Natural Scalpel", [
    ...brand(P.scalpel),
    sever(160, P.scalpel, { ...S, ms: 560, r: 1.5, size: 0.03, size2: 0.22, tone: -12 }),
    bloom(240, P.space, { ...S, ms: 240, r: 0.55, tone: 0.6 }),
  ], 760),

  // ── XULAN · ETERNAL NIGHTMARE ─────────────────────────────────────
  // "Dim your own Anima by 4; nullify 1 attack on this being or its
  // Soul." The most expensive defensive Impera in the game, and the
  // cost is the story: something is PAID before anything is refused.
  // So the embers rise off the card first — that is the Anima leaving —
  // and only then does the veil close. Read in that order it is a
  // sacrifice; in the other order it would be a shield with sparkles.
  p_xulan_3: score("Eternal Nightmare", [
    ...brand(P.violetDeep),
    embers(90, P.crimson, { ...S, ms: 460, n: 18, r: 0.5, size: 0.065, size2: 1.2 }),
    // The Anima burns off (embers, rising) and then the DREAD settles
    // (plume, falling and spreading). Two directions, and the card is
    // named for the second one.
    plume(300, P.shadow, { ...S, ms: 620, n: 30, r: 0.95, size: 0.1, size2: 1.2 }),
    veil(420, P.shadow, { ...S, ms: 620, r: 1.5, size: 0.9 }),
    glyph(430, P.violetPale, { ...S, ms: 460, tone: "✖", size: 34, r: 0.55 }),
    ...toll(P.violetDeep, 520, 2, "self"),
  ], 1060),

// ═══════════════════════════════════════════════════════════════════
// PHANTOM · BEAST
// ═══════════════════════════════════════════════════════════════════
// The three Spectral Tiger brands were authored in H23 Ship 1 as the
// proof line and they are reproduced here UNCHANGED — this file is now
// the single source of truth for all 36, and cardMotion re-exports it.
// Moving them was a one-source-of-truth job, not a rewrite; the scores
// caP'n already signed off on are byte-for-byte what they were.

  // "This being's Lyt Attacks can be directed at your adversary's Anima
  // instead of a being." Paws — two soft pads press down, close
  // together, and the seal takes.
  p_spectral_tiger_1: score("Spectral Paws", [
    shock(0, P.violetPale, { ...S, ms: 380, size: 1.7, r: 0.38, tone: 0.85 }),
    slash(130, P.phantom, { ...S, ms: 400, n: 2, r: 0.62, tone: 14 }),
    bloom(160, P.violetPale, { ...S, ms: 300, r: 0.7 }),
  ], 620),

  // "If this being's Lyt Attack dimmed Anima this turn, dim their Anima
  // by an additional 1." The claws come OUT — three marks, wider than
  // the paws, and a scatter as they set.
  p_spectral_tiger_2: score("Spectral Claws", [
    shock(0, P.violetPale, { ...S, ms: 360, size: 1.8, r: 0.4, tone: 0.85 }),
    slash(110, P.violetIce, { ...S, ms: 460, n: 3, r: 0.88, tone: 20 }),
    bloom(170, P.phantom, { ...S, ms: 280, r: 0.8 }),
    motes(220, P.violetPale, { ...S, ms: 400, n: 16, r: 1.1, size: 0.06 }),
  ], 700),

  // "Dim the Lyf of your adversary's cards by 2." Haunting PresentS —
  // the pun is canon and the gift travels: the brand takes, and then
  // something crosses the seam and touches everything over there.
  p_spectral_tiger_3: score("Haunting Presents", [
    shock(0, P.violetPale, { ...S, ms: 360, size: 1.9, r: 0.42, tone: 0.85 }),
    wave(130, P.phantom, { from: "self", to: "target", ms: 620, r: 5.4, size: 0.5, tone: 0.7 }),
    motes(330, P.violetPale, { ms: 500, n: 30, r: 2.2, size: 0.07 }),
    bloom(560, P.violetDeep, { ms: 300, r: 1.2 }),
  ], 900),

  // ── SHADOW FOX · HIDDEN PAWS ──────────────────────────────────────
  // "This being's Lyt Attacks can be directed at your adversary's Anima
  // instead of a being." The same EFFECT as Spectral Paws, and it must
  // not be the same score — that is the whole test of whether this
  // file is doing its job. The Tiger presses down and seals; the Fox
  // vanishes its own feet. `tone:"in"` on motes anchored to self is
  // the Fox's signature verb (its Lyt Attack, Nine Shadows, is built on
  // it) and it means *you have lost track of where this thing is*.
  p_shadow_fox_1: score("Hidden Paws", [
    ...brand(P.amethyst),
    motes(110, P.violetDeep, { ...S, ms: 520, n: 26, r: 1.3, size: 0.07, tone: "in" }),
    slash(420, P.amethyst, { ...S, ms: 360, n: 2, r: 0.5, tone: 10 }),
    bloom(440, P.violetPale, { ...S, ms: 260, r: 0.62 }),
  ], 800),

  // ── SHADOW FOX · SHADOW FUR ───────────────────────────────────────
  // "This being is Intangible." Intangibility #2, and the contrast with
  // H-01's Natural Glow is deliberate and total: H-01 becomes light,
  // the Fox becomes dark. Same verb, opposite value, and the palette
  // does the telling.
  p_shadow_fox_2: score("Shadow Fur", [
    ...brand(P.amethyst),
    motes(100, P.shadow, { ...S, ms: 540, n: 34, r: 1.5, size: 0.08, tone: "in" }),
    veil(300, P.violetDeep, { ...S, ms: 600, r: 1.3, size: 0.72 }),
  ], 840),

  // ── SHADOW FOX · LORD OF SHADOWS ──────────────────────────────────
  // "This being is Intangible." Intangibility #3 — and this is the LORD
  // doing it, "Rulers of Shadow Spheres... loyalty amongst only the
  // most malevolent of D'aeterni." Same shape as Shadow Fur, one rank
  // louder: the dark converges from twice as far, the dome is bigger,
  // and it wears a crown. Deliberately a variation rather than a new
  // idea — this is one species stating the same thing with more
  // authority, and inventing a fresh image would have hidden that.
  p_shadow_fox_3: score("Lord of Shadows", [
    ...brand(P.violetDeep),
    motes(90, P.shadow, { ...S, ms: 620, n: 46, r: 2.6, size: 0.085, tone: "in" }),
    veil(320, P.violetDeep, { ...S, ms: 680, r: 1.65, size: 0.9 }),
    halo(400, P.amethyst, { ...S, ms: 700, r: 1.05, size: 0.82, spin: 1.2 }),
    ...toll(P.shadow, 480, 2, "self"),
  ], 1120),

  // ── INVERTED SERPENT · PHANTOM FANGS ──────────────────────────────
  // "This being's Lyt Attacks can be directed at your adversary's Anima
  // instead of a being." Intangible-to-Anima #3, and the third distinct
  // answer to it: the Tiger presses, the Fox vanishes, the Serpent
  // simply OPENS ITS MOUTH. Two punctures eight degrees apart — fangs,
  // not claws — and the beam is the strike line it has just been given
  // permission to draw straight to the adversary.
  p_inverted_serpent_1: score("Phantom Fangs", [
    ...brand(P.violetIce),
    slash(120, P.violetIce, { ...S, ms: 400, n: 2, r: 0.52, tone: 8 }),
    beam(260, P.amethyst, { from: "self", to: "target", ms: 260, size: 0.022 }),
    bloom(300, P.phantom, { ms: 260, r: 0.7 }),
  ], 780),

  // ── INVERTED SERPENT · SUPERNATURAL BITE ──────────────────────────
  // "Dim your adversary's Anima by 1, whenever this being Lyt Attacks."
  // The species' whole identity is constriction — its Lyt Attack is The
  // Coil — so the brand is the coil TIGHTENING before the bite, which
  // is the same two-beat shape as the attack at a third of the length.
  p_inverted_serpent_2: score("Supernatural Bite", [
    ...brand(P.amethyst),
    spiral(110, P.amethyst, { ...S, ms: 480, spin: 2.2, r: 0.72, size: 1.0 }),
    slash(430, P.violetIce, { ...S, ms: 340, n: 2, r: 0.5, tone: 8 }),
    ...toll(P.violetDeep, 460, 1, "self"),
  ], 840),

  // ── INVERTED SERPENT · INVERTED SCALES ────────────────────────────
  // "Dim your adversary's Anima by 3, for every being they have that is
  // currently Intangible." The card punishes the exact thing five other
  // Imperas in this file grant, which is a lovely piece of design and
  // the score gets to say it: the rune runs `tone:"close"` — the ONLY
  // Impera that closes rather than opens — and then a wave crosses to
  // the adversary's half, because the effect is aimed over there. An
  // inversion, drawn as an inversion.
  p_inverted_serpent_3: score("Inverted Scales", [
    ...brand(P.violetDeep),
    rune(100, P.violetIce, { ...S, ms: 620, r: 1.3, n: 12, spin: 0.6, tone: "close" }),
    wave(420, P.phantom, { from: "self", to: "target", ms: 560, r: 5.0, size: 0.48, tone: 0.7 }),
    ...wound(P.amethyst, 780, 2),
  ], 1060),

// ═══════════════════════════════════════════════════════════════════
// TIME · MACHINE
// ═══════════════════════════════════════════════════════════════════

  // ── Z4Z · RELENTLESS ──────────────────────────────────────────────
  // "Dim your own Anima by 5; this being gains an additional Lyt
  // Attack." Extra-attack effects are the Time Ilk's signature and they
  // all share one verb here — ECHOES, the ghost copies that H21's
  // strobed lunge always deserved and never had. Relentless is the
  // cheapest of the three, so: one echo, and the Anima it cost burning
  // off first.
  p_z4z_1: score("Relentless", [
    ...brand(P.time),
    embers(80, P.crimson, { ...S, ms: 460, n: 18, r: 0.5, size: 0.065, size2: 1.2 }),
    echoes(260, P.time, { from: "self", to: "target", ms: 520, n: 3, size: 0.5, tone: 0.4 }),
    ...toll(P.emberGold, 620, 1, "self"),
  ], 900),

  // ── Z4Z · CATASTROPHE ─────────────────────────────────────────────
  // "If you have another Lvl 2 Time Being, this being gains an
  // additional Lyt Attack." Conditional on a SECOND machine existing,
  // and named Catastrophe — so the ground goes first. The crack is
  // small here and enormous in Apocalypse below; that escalation across
  // one species' three brands is the point of authoring them together.
  p_z4z_2: score("Catastrophe", [
    ...brand(P.rust),
    crack(110, P.time, { ...S, ms: 620, n: 5, r: 1.9 }),
    echoes(300, P.emberGold, { from: "self", to: "target", ms: 520, n: 4, size: 0.52, tone: 0.45 }),
    ...toll(P.rust, 660, 2, "self"),
  ], 980),

  // ── Z4Z · APOCALYPSE ──────────────────────────────────────────────
  // "Dim your own Anima by 8; your beings can ALL make an additional
  // Lyt Attack." The most expensive Impera in the game and the only one
  // that arms the entire board, so it is the one Impera permitted to be
  // nearly U'hlti-loud. The wave is what makes it read as *everyone* —
  // it leaves this card and crosses the whole field, and every other
  // extra-attack effect in the file deliberately does not do that.
  p_z4z_3: score("Apocalypse", [
    ...brand(P.crimson),
    embers(70, P.crimson, { ...S, ms: 520, n: 30, r: 0.7, size: 0.08, size2: 1.7 }),
    crack(180, P.rust, { ...S, ms: 720, n: 9, r: 3.4 }),
    echoes(360, P.time, { from: "self", to: "target", ms: 560, n: 5, size: 0.55, tone: 0.5 }),
    wave(420, P.emberGold, { from: "self", to: "back", ms: 600, r: 5.6, size: 0.55, tone: 0.7 }),
    ...toll(P.crimson, 700, 3, "self"),
  ], 1320, { shake: SHAKE.hard(180) , surge: SURGE.soft(P.crimson, 180) }),

  // ── DG-0 · HOPE ───────────────────────────────────────────────────
  // "Gain +1 Anima, whenever a new Time Being enters combat." The only
  // purely generous Impera in the file, from a line that has "healed 1%
  // of dying beings in the Unstable World." Embers rising and nothing
  // else — no cut, no ring, no mark. It is the quietest score in the
  // game and it is supposed to be: hope is not an event.
  p_dg0_1: score("Hope", [
    ...brand(P.judgeGold),
    embers(120, P.judgeGold, { ...S, ms: 680, n: 26, r: 0.75, size: 0.075, size2: 1.9 }),
    halo(300, P.emberGold, { ...S, ms: 560, r: 0.85, size: 0.7, spin: 0.9 }),
  ], 900),

  // ── DG-0 · LAW AND ORDER ──────────────────────────────────────────
  // "Dim your own Anima by 1; draw 1 card." One Khao, the cheapest card
  // in the game. A gavel: the mark comes down, one hard ring, and a
  // rift opens for exactly as long as it takes one card through. Short
  // and procedural on purpose — this is paperwork.
  p_dg0_2: score("Law and Order", [
    ...brand(P.brass),
    glyph(90, P.judgeGold, { ...S, ms: 380, tone: "⚖", size: 30, r: 0.45 }),
    shock(300, P.brass, { ...S, ms: 340, size: 0.16, r: 1.4, tone: 0.85 }),
    rift(320, P.emberGold, { ...S, ms: 400, r: 1.1, size: 0.03, size2: 0.26 }),
  ], 740),

  // ── DG-0 · JUSTICE PREVAILS ───────────────────────────────────────
  // "Draw 1 card, if you gained Anima this turn." The Lord's brand from
  // the line whose Lyt Attack is The Verdict, and it borrows the
  // Verdict's grammar deliberately: the light comes DOWN. A pillar, a
  // seal, a star. "If seen, it's usually in response to an Apocalypse
  // level situation."
  p_dg0_3: score("Justice Prevails", [
    ...brand(P.judgeGold),
    pillar(90, P.judgeGold, { ...S, ms: 600, r: 4.6, size: 0.44, spin: 0.25 }),
    rune(200, P.emberGold, { ...S, ms: 560, r: 1.15, n: 12, spin: 0.4 }),
    star(520, P.judgeGold, { ...S, ms: 380, r: 1.5, size: 0.05 }),
    ...toll(P.brass, 560, 2, "self"),
  ], 1060),

  // ── ANTDROID · RESTLESS ───────────────────────────────────────────
  // "Dim your own Anima by 5; this being may Lyt Attack again, but only
  // to dim Anima." The Antdroid line's verb is the STREAM — its Lyt
  // Attack, The Swarm, is a continuous column of small things crossing
  // the gap — and this brand is that column being pointed at the
  // Warlock instead of at a being, which is precisely what the effect
  // text says. The stream terminates at `target`, and for this card
  // target IS the enemy plate.
  p_antdroid_1: score("Restless", [
    ...brand(P.swarm),
    embers(80, P.crimson, { ...S, ms: 440, n: 16, r: 0.5, size: 0.06, size2: 1.1 }),
    stream(230, P.time, { from: "self", to: "target", ms: 560, n: 40, r: 0.2, size: 0.055 }),
    bloom(680, P.swarm, { ms: 280, r: 0.9 }),
  ], 980),

  // ── ANTDROID · EQUIVALENT EXCHANGE ────────────────────────────────
  // "Dim your own Anima by 4; draw 2 cards." A trade, stated as one —
  // and CHAIN is the only primitive in the vocabulary with two ends,
  // which is exactly why it was built. The chain snaps taut at the
  // moment the deal closes and the two rifts open on the far side of
  // that snap: paid, then received, in that order and visibly.
  p_antdroid_2: score("Equivalent Exchange", [
    ...brand(P.brass),
    chain(100, P.swarm, { from: "self", to: "target", ms: 560, r: 0.8, size: 0.095 }),
    rift(430, P.emberGold, { ...S, ms: 380, r: 1.0, size: 0.03, size2: 0.22 }),
    rift(520, P.emberGold, { ...S, ms: 380, r: 1.0, size: 0.03, size2: 0.22, spin: 0.6 }),
  ], 940),

  // ── ANTDROID · KING OF THE ANTHILL ────────────────────────────────
  // "This being can make an additional Lyt Attack, for every other
  // Antdroid being in combat." "It is an Ant's duty to conquer. Since
  // the initial unfolding of Time, they have, and until the final
  // folding of time, they will." So: the hill is BUILT (lattice — the
  // only non-Construction card in the game that earns it, because an
  // anthill is architecture), the swarm answers, and the king is
  // crowned.
  p_antdroid_3: score("King of the Anthill", [
    ...brand(P.swarm),
    lattice(110, P.brass, { ...S, ms: 640, r: 0.9, n: 12, size: 0.05 }),
    stream(380, P.time, { from: "back", to: "self", ms: 520, n: 46, r: 0.26, size: 0.055 }),
    halo(560, P.swarm, { ...S, ms: 620, r: 1.0, size: 0.82, spin: 1.3 }),
    ...toll(P.brass, 620, 2, "self"),
  ], 1180),

// ═══════════════════════════════════════════════════════════════════
// DRAGON · DRAGON
// ═══════════════════════════════════════════════════════════════════

  // ── LUCKY DRAGON · PAY DAY ────────────────────────────────────────
  // "Dim your own Anima by 1. Flip a coin and choose your side; if it's
  // your side, draw 1 card; if not, your adversary draws 1 as well."
  // The whole Lucky Dragon line is coin flips and the COIN primitive
  // was built for these three cards and the Wager. One Khao, one coin,
  // no ornament — the cheapest gamble in the game, and the hang before
  // it lands is the entire experience of it.
  p_lucky_dragon_1: score("Pay Day", [
    ...brand(P.jynxi),
    coin(120, P.swarm, { ...S, ms: 640, r: 1.4, size: 0.28, spin: 7 }),
    ...toll(P.jynxi, 660, 1, "self"),
  ], 900),

  // ── LUCKY DRAGON · BLACK CAT'S BEST FRIEND ────────────────────────
  // "Flip a coin... draw 2 cards, if not, your adversary draws 2 as
  // well." Twice the stakes of Pay Day, so: a higher throw, a longer
  // hang, and TWO rifts opening after it lands. The escalation across
  // the three Lucky Dragon brands is entirely in the coin's altitude
  // and airtime, which is a nice accident of the primitive — a bigger
  // bet is literally a bigger toss.
  p_lucky_dragon_2: score("Black Cat's Best Friend", [
    ...brand(P.jynxi),
    coin(110, P.jynxi, { ...S, ms: 760, r: 2.0, size: 0.31, spin: 9 }),
    rift(700, P.jynxi, { ...S, ms: 360, r: 0.95, size: 0.03, size2: 0.2, tone: -8 }),
    rift(780, P.jynxi, { ...S, ms: 360, r: 0.95, size: 0.03, size2: 0.2, tone: 8 }),
  ], 1160),

  // ── LUCKY DRAGON · FATE MAKER ─────────────────────────────────────
  // "If 1 of your cards is going to be Lyt Attacked, flip a coin...
  // reflect the Lyt Attack to your adversary's Anima." A gamble AND a
  // reflection, and it is the only card in the game that turns an
  // attack around. So the coin lands, a veil comes up, and then a beam
  // leaves toward the adversary — the incoming attack going back the
  // way it came. Three verbs, in the order the effect resolves them.
  p_lucky_dragon_3: score("Fate Maker", [
    ...brand(P.swarm),
    coin(90, P.swarm, { ...S, ms: 680, r: 1.7, size: 0.3, spin: 8 }),
    // The only card in Terror that turns an attack around. A veil and
    // then a beam read as two events — blocked, and then separately
    // attacked. `mirror` is one: the surface forms where the hit lands
    // and the same flash leaves along the path it arrived on.
    mirror(620, P.swarm, { from: "target", to: "self", ms: 620, r: 1.15 }),
    ...wound(P.swarm, 1060, 2),
  ], 1360),

  // ── PREMIER DRAGON · GOOD MORNING ─────────────────────────────────
  // "This being gains +1 Lyt and +1 Lyf, for every Dragon Being you
  // have in combat." "The First & Only Leader of the Unstable World,
  // when born, the surrounding area is briefly affected by Bloom." The
  // card is named for a sunrise and its own flavour text uses the word
  // Bloom, so this is a DAWN: a warm column, heat rising into it, and
  // light spreading outward. No violence anywhere in it.
  p_premier_dragon_1: score("Good Morning", [
    ...brand(P.roseSoft),
    pillar(100, P.roseSoft, { ...S, ms: 660, r: 4.4, size: 0.46, spin: 0.2 }),
    embers(200, P.emberGold, { ...S, ms: 600, n: 26, r: 0.8, size: 0.07, size2: 1.8 }),
    wave(420, P.scale, { from: "self", to: "back", ms: 560, r: 4.6, size: 0.44, tone: 0.5 }),
  ], 1020),

  // ── PREMIER DRAGON · SOCIAL DRAGONFLY ─────────────────────────────
  // "This being gains +1 Lyt, for every Dragon Being in combat." Note
  // "in combat" and not "you have" — this one counts the ADVERSARY's
  // dragons too, which is the joke in the name. So it is the only buff
  // in the file that reaches across the seam to gather: echoes running
  // the wrong way, from target back to self.
  p_premier_dragon_2: score("Social Dragonfly", [
    ...brand(P.scale),
    echoes(110, P.roseSoft, { from: "target", to: "self", ms: 560, n: 4, size: 0.48, tone: 0.45 }),
    star(520, P.dragon, { ...S, ms: 380, r: 1.4, size: 0.05 }),
    ...toll(P.scale, 560, 1, "self"),
  ], 920),

  // ── PREMIER DRAGON · DESTINED FOR GREATNESS ───────────────────────
  // "This being is Intangible to Impera and U'hltis." Intangibility
  // #4, and the most specific of the five — it is not immune to
  // ATTACKS, it is immune to the ATTACHMENT LADDER. So the dome is
  // paired with a halo, because what this card is refusing is precisely
  // the class of thing that crowns a being. It cannot be crowned by
  // anyone else because it already is.
  p_premier_dragon_3: score("Destined for Greatness", [
    ...brand(P.goldPale),
    veil(110, P.roseSoft, { ...S, ms: 640, r: 1.45, size: 0.85 }),
    halo(260, P.goldPale, { ...S, ms: 760, r: 1.1, size: 0.86, spin: 1.0 }),
    glyph(360, P.goldPale, { ...S, ms: 480, tone: "✦", size: 38, r: 0.6 }),
    ...toll(P.dragon, 460, 2, "self"),
  ], 1120),

  // ── CHAOS DRAGON · NATURAL DISASTER ───────────────────────────────
  // "Ruin 1 Non-Being." The first destruction Impera, and destruction
  // has exactly one grammar in this game: CRACK then SHATTER. The
  // ground fails, and then the thing standing on it comes apart. Every
  // ruin effect in every file uses that pair in that order, which is
  // how a player learns to read "something just died" without a label.
  p_chaos_dragon_1: score("Natural Disaster", [
    ...brand(P.crimson),
    crack(120, P.bloodDeep, { ms: 560, n: 7, r: 2.4 }),
    thorn(260, P.crimson, { ms: 560, n: 8, r: 1.15, size: 1.0 }),
    shatter(420, P.crimson, { ms: 620, n: 14, r: 1.5 }),
    ...toll(P.roseSoft, 420, 2),
  ], 1020),

  // ── CHAOS DRAGON · THRIVING IN CHAOS ──────────────────────────────
  // "Dim your adversary's Anima by 1, whenever they Lyt Attack." A
  // standing curse laid on the OTHER side of the table — one of very
  // few Imperas whose lasting effect lives over there — so the rune is
  // inscribed at `target`, closing, and the embers burn on their half.
  // The brand still presses into this card's own Soul, because that is
  // where the Impera lives; the consequence is what travels.
  p_chaos_dragon_2: score("Thriving in Chaos", [
    ...brand(P.bloodDeep),
    rune(140, P.crimson, { ms: 660, r: 1.5, n: 10, spin: 0.7, tone: "close" }),
    embers(420, P.bloodDeep, { ms: 540, n: 22, r: 0.9, size: 0.07, size2: 1.4 }),
    ...toll(P.crimson, 560, 2),
  ], 1080),

  // ── CHAOS DRAGON · NONE MORE THAN I ───────────────────────────────
  // "Beings with more than 10 Lyt are instantly ruined and sent to
  // Necropolis." A board-wide execution clause, from "The One & Only,
  // Xavizu the A'oeternis... admitted that they are a Threat." So this
  // is the loudest Impera in the file after Apocalypse, and the only
  // one that uses SEVER — the execution verb, reserved across every
  // score file for effects that do not wound but END. The wave carries
  // it to the whole board because the clause applies to every being
  // alive, including this card's own side.
  p_chaos_dragon_3: score("None More Than I", [
    ...brand(P.crimson),
    crack(140, P.bloodDeep, { ...S, ms: 700, n: 8, r: 3.0 }),
    wave(300, P.crimson, { from: "self", to: "target", ms: 620, r: 6.0, size: 0.56, tone: 0.8 }),
    sever(640, P.roseSoft, { ms: 620, r: 2.2, size: 0.05, size2: 0.34, tone: -16 }),
    ...toll(P.bloodDeep, 700, 3),
  ], 1400, { shake: SHAKE.heavy(640) , surge: SURGE.full(P.bloodDeep, 640) }),
};

// ── THE CODEX VIEW ─────────────────────────────────────────────────
export const describeImperas = () =>
  Object.keys(IMPERAS).map((k) => ({
    channel: "IMPERA", key: k, name: IMPERAS[k].name, ms: IMPERAS[k].ms,
    cues: IMPERAS[k].cues.length,
  }));

export default IMPERAS;
